import { nanoid } from '@reduxjs/toolkit';

/**
 * Singleton Toast Manager Class
 * Centralized toast management with proper encapsulation
 *
 * @author @buunsal
 */
class ToastManager {
	constructor() {
		if (ToastManager.instance) {
			return ToastManager.instance;
		}

		this.toasts = [];
		this.subscribers = new Set();
		this.maxDisplayToasts = 3;

		ToastManager.instance = this;
	}

	/**
	 * Subscribe to toast changes
	 * @param {Function} callback - Callback function to call when toasts change
	 * @returns {Function} Unsubscribe function
	 */
	subscribe(callback) {
		this.subscribers.add(callback);

		// Return unsubscribe function
		return () => {
			this.subscribers.delete(callback);
		};
	}

	/**
	 * Notify all subscribers of toast changes
	 */
	notify() {
		this.subscribers.forEach((callback) => {
			callback([...this.toasts]);
		});
	}

	/**
	 * Add a new toast
	 * @param {Object} toast - Toast configuration
	 * @returns {string} Toast ID
	 */
	add(toast) {
		const newToast = {
			id: nanoid(),
			type: 'info',
			duration: 7000,
			persist: false,
			...toast,
			timestamp: Date.now()
		};

		this.toasts.push(newToast);
		this.notify();
		return newToast.id;
	}

	/**
	 * Remove a toast by ID
	 * @param {string} id - Toast ID
	 */
	remove(id) {
		const initialLength = this.toasts.length;
		this.toasts = this.toasts.filter((toast) => toast.id !== id);

		// Only notify if something was actually removed
		if (this.toasts.length !== initialLength) {
			this.notify();
		}
	}

	/**
	 * Clear all toasts
	 */
	clear() {
		if (this.toasts.length > 0) {
			this.toasts = [];
			this.notify();
		}
	}

	/**
	 * Get current toasts
	 * @returns {Array} Current toasts array
	 */
	getToasts() {
		return [...this.toasts];
	}

	/**
	 * Get visible toasts for display (limited by maxDisplayToasts)
	 * Returns first 3 toasts in reverse order for proper UI display
	 * @returns {Array} Visible toasts array (newest first for UI)
	 */
	getVisibleToasts() {
		// Get first 3 toasts (oldest) and reverse for display (newest on top)
		return [...this.toasts].slice(0, this.maxDisplayToasts).reverse();
	}
}

// Create and export singleton instance
const toastManager = new ToastManager();
export default toastManager;
